import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import { Navigate } from 'react-router-dom';
import { Users, Settings, Tag, CreditCard, AlertCircle, FileText, Shield } from 'lucide-react';

// Yönetim Paneli Bileşenleri
import UserManager from '../components/admin/UserManager';
import StatusManager from '../components/admin/StatusManager';
import MethodManager from '../components/admin/MethodManager';
import ReasonManager from '../components/admin/ReasonManager';
import RoleManager from '../components/admin/RoleManager';

const AdminPanelPage = () => {
  const { user } = useContext(AuthContext);
  const { hasPermission, getUserPermissions } = usePermission();
  const [activeTab, setActiveTab] = useState('users');
  const [error, setError] = useState(null);
  
  // Daha detaylı izin bilgisi
  const hasViewAdminPanel = hasPermission('view_admin_panel');
  const hasManageUsers = hasPermission('manage_users');
  const hasManageRoles = hasPermission('manage_roles');
  const hasManageStatuses = hasPermission('manage_statuses');
  const hasManagePaymentMethods = hasPermission('manage_payment_methods');
  const hasManageReasons = hasPermission('manage_reasons');
  
  // Check for specific permissions OR the general admin panel permission
  const hasAdminAccess = hasViewAdminPanel || 
    hasManageUsers || 
    hasManageRoles || 
    hasManageStatuses || 
    hasManagePaymentMethods || 
    hasManageReasons;
  
  // Debug: İzin ve erişim kontrolü detaylarını logla
  useEffect(() => {
    console.log('AdminPanelPage - Kullanıcı izinleri:', getUserPermissions());
    console.log('AdminPanelPage - İzin kontrolleri:', {
      view_admin_panel: hasViewAdminPanel,
      manage_users: hasManageUsers,
      manage_roles: hasManageRoles,
      manage_statuses: hasManageStatuses,
      manage_payment_methods: hasManagePaymentMethods,
      manage_reasons: hasManageReasons,
      hasAdminAccess: hasAdminAccess
    });
    
    if (user) {
      console.log('AdminPanelPage - Kullanıcı bilgisi:', {
        id: user.id,
        username: user.username,
        role_id: user.role_id
      });
    }
  }, [
    user,
    hasViewAdminPanel,
    hasManageUsers,
    hasManageRoles,
    hasManageStatuses,
    hasManagePaymentMethods,
    hasManageReasons,
    hasAdminAccess,
    getUserPermissions
  ]);
  
  // İzin durumunu kontrol et ve yüklü olduğundan emin ol
  useEffect(() => {
    if (user && !hasAdminAccess) {
      const permissionList = getUserPermissions();
      console.error('AdminPanelPage - Erişim reddedildi:', { 
        hasAdminAccess,
        userPermissions: permissionList,
        requiredPermissions: [
          'view_admin_panel', 'manage_users', 'manage_roles', 
          'manage_statuses', 'manage_payment_methods', 'manage_reasons'
        ]
      });
      
      setError('Bu sayfaya erişim için yönetim yetkileri gereklidir');
    } else {
      setError(null);
    }
  }, [user, hasAdminAccess, getUserPermissions]);
  
  // Sadece yönetim paneli erişim izni olanlar sayfayı görebilir
  if (!user) {
    console.log('AdminPanelPage - Kullanıcı giriş yapmamış, yönlendiriliyor');
    return <Navigate to="/login" replace />;
  }
  
  if (!hasAdminAccess) {
    console.log('AdminPanelPage - Kullanıcının erişim izni yok, yönlendiriliyor');
    return <Navigate to="/dashboard" replace />;
  }
  
  // Aktif sekme içeriğini render et
  const renderTabContent = () => {
    // Hata varsa göster
    if (error) {
      return (
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <AlertCircle size={24} className="inline-block mr-2" />
          {error}
        </div>
      );
    }
    
    // Her sekme için ilgili bileşeni göster
    switch (activeTab) {
      case 'users':
        // Check for user management permission
        if (hasManageUsers || hasViewAdminPanel) {
          return <UserManager />;
        } else {
          return (
            <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
              <AlertCircle size={24} className="inline-block mr-2" />
              Kullanıcı yönetimi için yeterli yetkiniz bulunmamaktadır
            </div>
          );
        }
      case 'roles':
        // Rol yönetimi için ek izin kontrolü
        if (hasManageRoles || hasViewAdminPanel) {
          return <RoleManager />;
        } else {
          return (
            <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
              <AlertCircle size={24} className="inline-block mr-2" />
              Rol yönetimi için yeterli yetkiniz bulunmamaktadır
            </div>
          );
        }
      case 'statuses':
        // Check for status management permission
        if (hasManageStatuses || hasViewAdminPanel) {
          return <StatusManager />;
        } else {
          return (
            <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
              <AlertCircle size={24} className="inline-block mr-2" />
              Durum yönetimi için yeterli yetkiniz bulunmamaktadır
            </div>
          );
        }
      case 'methods':
        // Check for payment methods management permission
        if (hasManagePaymentMethods || hasViewAdminPanel) {
          return <MethodManager />;
        } else {
          return (
            <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
              <AlertCircle size={24} className="inline-block mr-2" />
              Ödeme yöntemleri yönetimi için yeterli yetkiniz bulunmamaktadır
            </div>
          );
        }
      case 'reasons':
        // Check for reasons management permission
        if (hasManageReasons || hasViewAdminPanel) {
          return <ReasonManager />;
        } else {
          return (
            <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md border border-yellow-200">
              <AlertCircle size={24} className="inline-block mr-2" />
              İade sebepleri yönetimi için yeterli yetkiniz bulunmamaktadır
            </div>
          );
        }
      case 'settings':
        return <div>Genel Ayarlar Geliştiriliyor</div>;
      default:
        return <div>Sekme içeriği bulunamadı</div>;
    }
  };
  
  return (
    <div className="py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Yönetim Paneli</h1>
        <p className="text-gray-600">Sistem ayarlarını ve kullanıcıları yönetin</p>
      </div>
      
      {/* Admin Sekmeleri */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            {/* Kullanıcı Yönetimi Sekmesi */}
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users size={18} className="mr-2" />
              Kullanıcı Yönetimi
            </button>
            
            {/* Rol Yönetimi Sekmesi */}
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'roles'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield size={18} className="mr-2" />
              Rol Yönetimi
            </button>
            
            {/* Durum Yönetimi Sekmesi */}
            <button
              onClick={() => setActiveTab('statuses')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'statuses'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag size={18} className="mr-2" />
              Durum Yönetimi
            </button>
            
            {/* Ödeme Yöntemleri Sekmesi */}
            <button
              onClick={() => setActiveTab('methods')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'methods'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard size={18} className="mr-2" />
              Ödeme Yöntemleri
            </button>
            
            {/* İade Sebepleri Sekmesi */}
            <button
              onClick={() => setActiveTab('reasons')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'reasons'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText size={18} className="mr-2" />
              İade Sebepleri
            </button>
            
            {/* Genel Ayarlar Sekmesi */}
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'settings'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings size={18} className="mr-2" />
              Genel Ayarlar
            </button>
          </nav>
        </div>
      </div>
      
      {/* Sekme İçeriği */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'settings' ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle size={48} className="text-orange-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Genel Ayarlar Geliştiriliyor</h2>
            <p className="text-gray-600 max-w-lg mx-auto">
              Bu bölüm şu anda geliştirme aşamasındadır. Yakında sistem ayarları, bildirim yönetimi ve daha fazla özellik burada yer alacaktır.
            </p>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>
    </div>
  );
};

export default AdminPanelPage;